<template>
  <div class="crear-solicitud-page" :style="propertyManagerThemeStyles">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>
    <ModalPerfilProfesional ref="modalPerfil" :categorias="categorias" />
    <div class="column-right">
      <!-- 1) NEW: 10px padding wrapper from title to bottom -->
      <div class="content-pad">
        <div class="page-heading">
          <h2>Crear Nueva Solicitud</h2>
        </div>

        <!-- ===== 2-column app grid ===== -->
        <form class="new-solicitud-grid" @submit.prevent="crearSolicitud" enctype="multipart/form-data">
          <!-- LEFT: FORM SECTIONS -->
          <section class="left-col">

            <!-- Ubicación y programación -->
            <div class="card">
              <div class="card-header"><h3>Ubicación y programación</h3></div>
              <div class="card-body two-col">
                <div v-if="propiedades.length" class="form-group span-2">
                  <label>Ubicación del servicio</label>
                  <div class="location-toggle">
                    <button
                      type="button"
                      class="location-toggle__btn"
                      :class="{ 'is-active': ubicacionModo === 'propiedad' }"
                      @click="setUbicacionModo('propiedad')"
                    >
                      Propiedad guardada
                    </button>
                    <button
                      type="button"
                      class="location-toggle__btn"
                      :class="{ 'is-active': ubicacionModo === 'comuna' }"
                      @click="setUbicacionModo('comuna')"
                    >
                      Dirección manual
                    </button>
                  </div>
                </div>

                <div v-if="propiedades.length && ubicacionModo === 'propiedad'" class="form-group span-2">
                  <label>Propiedad guardada</label>
                  <select v-model="selectedPropiedadId" @change="handlePropiedadChange">
                    <option v-for="propiedad in propiedades" :key="propiedad.id" :value="propiedad.id">
                      {{ formatPropiedadLabel(propiedad) }}
                    </option>
                  </select>
                  <p class="helper-text">Usaremos la información registrada en la propiedad seleccionada.</p>
                </div>

                <div v-if="useSavedProperty && selectedPropiedad" class="property-summary span-2">
                  <div>
                    <span class="property-summary__label">Dirección</span>
                    <span class="property-summary__value">{{ selectedPropiedad.direccion || 'Sin dirección' }}</span>
                  </div>
                  <div>
                    <span class="property-summary__label">Comuna</span>
                    <span class="property-summary__value">{{ getComunaName(selectedPropiedad) || 'Sin comuna' }}</span>
                  </div>
                  <div>
                    <span class="property-summary__label">Tipo</span>
                    <span class="property-summary__value">{{ selectedPropiedad.tipo_propiedad || 'Sin tipo' }}</span>
                  </div>
                  <div v-if="selectedPropiedad.numero_apartamento">
                    <span class="property-summary__label">Número</span>
                    <span class="property-summary__value">{{ selectedPropiedad.numero_apartamento }}</span>
                  </div>
                </div>

                <div class="form-group">
                  <label>Comuna</label>
                  <select v-model="solicitud.comuna_id" :disabled="useSavedProperty" :required="!useSavedProperty">
                    <option disabled value="">Seleccione una comuna</option>
                    <option v-for="c in comunas" :key="c.id" :value="c.id">{{ c.Nombre }}</option>
                  </select>
                </div>

                <div class="form-group address-group span-1-2">
                  <label style="display:inline-flex; align-items:center; gap:6px;">
                    <span>Dirección</span> <small>(elige de la lista)</small>
                  </label>
                  <input
                    v-model="solicitud.direccion"
                    type="text"
                    :required="!useSavedProperty"
                    :disabled="useSavedProperty"
                    @input="onInput"
                    placeholder="Empieza a escribir la dirección…"
                  >
                  <ul v-if="suggestions.length" class="suggestions">
                    <li v-for="(s, i) in suggestions" :key="i" @click="selectSuggestion(s)">
                      {{ s.description }}
                    </li>
                  </ul>
                </div>

                <div class="form-group">
                  <label>Tipo de propiedad</label>
                  <select v-model="solicitud.tipo_propiedad" :disabled="useSavedProperty" :required="!useSavedProperty">
                    <option disabled value="">Seleccione</option>
                    <option value="Casa">Casa</option>
                    <option value="Departamento">Departamento</option>
                  </select>
                </div>
                <div class="form-group" v-if="solicitud.tipo_propiedad === 'Departamento'">
                  <label>Nº de departamento</label>
                  <input v-model="solicitud.numero_apartamento" :required="!useSavedProperty">
                </div>

              </div>
            </div>

            <!-- Contacto en la propiedad -->
            <div class="card">
              <div class="card-header">
                <h3>Contacto en la propiedad</h3>
              </div>
              <div class="card-body two-col">
                <div class="form-group">
                  <label>Nombre</label>
                  <input v-model="usuario.nombre" type="text" required>
                </div>
                <div class="form-group">
                  <label>Correo</label>
                  <input v-model="usuario.email" type="email" required>
                </div>
                <div class="form-group span-2">
                  <label>Teléfono</label>
                  <input v-model="usuario.telefono" type="text" required>
                </div>
                <div class="form-group span-2">
                  <label class="seg">
                    <input type="checkbox" v-model="usuario.notificar_cliente">
                    <span>Enviar notificaciones</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Detalle del servicio -->
            <div class="card">
              <div class="card-header">
                <h3>Detalle del servicio</h3>
              </div>
              <div class="card-body two-col">
                <div class="form-group">
                  <label>Prioridad</label>
                  <select v-model="solicitud.prioridad" required>
                    <option disabled value="">Seleccione</option>
                    <option value="alta">Alta</option>
                    <option value="media">Media</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>

                <div class="form-group">
                  <label>Estado de la propiedad</label>
                  <select v-model="solicitud.estado" required>
                    <option disabled value="">Seleccione</option>
                    <option value="habitada">Habitada</option>
                    <option value="inhabitada">Inhabitada</option>
                    <option value="desconocido">Desconocido</option>
                  </select>
                </div>

                <!-- Habilitar chat -->
                <div class="form-group chat-toggle">
                  <label class="seg">
                    <input type="checkbox" v-model="solicitud.chat_habilitado">
                    <span>Habilitar chat</span>
                  </label>
                </div>

                <div class="form-group span-2">
                  <label>Descripción</label>
                  <textarea v-model="solicitud.descripcion" rows="5" required placeholder="Describe el trabajo, materiales, alturas, etc."></textarea>
                </div>

                <!-- 2) NEW: Dropzone replacing file input -->
                <div class="form-group span-2">
                  <label>Fotos / archivos</label>
                  <div
                    class="dropzone"
                    :class="{ 'is-dragover': dragOver }"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="handleDrop"
                  >
                    <p class="dz-text">
                      Arrastra y suelta archivos aquí o
                      <button type="button" class="link-btn" @click="triggerFilePicker">explora</button>
                    </p>
                    <input ref="fileInput" type="file" multiple @change="onFileChange" style="display:none">
                    <div v-if="fotos.length" class="thumbs">
                      <div v-for="(f,i) in fotos" :key="i" class="thumb">
                        <div class="thumb-icon" v-if="!isImage(f)">📄</div>
                        <img v-else :src="thumbURL(f)" :alt="f.name" />
                        <div class="thumb-name" :title="f.name">{{ f.name }}</div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- Presupuesto y requisitos -->
            <div class="card">
              <div class="card-header"><h3>Presupuesto y requisitos</h3></div>
              <div class="card-body two-col">
                <div class="form-group span-2">
                  <label>Ubicación de llaves</label>
                  <div class="radio-row">
                    <label><input type="radio" v-model="solicitud.ubicacion_llaves" value="Arrendatario"> Arrendatario</label>
                    <label><input type="radio" v-model="solicitud.ubicacion_llaves" value="Propietario"> Propietario</label>
                    <label><input type="radio" v-model="solicitud.ubicacion_llaves" value="Conserjería"> Conserjería</label>
                    <label><input type="radio" v-model="solicitud.ubicacion_llaves" value="Corredora"> Corredora</label>
                    <label><input type="radio" v-model="solicitud.ubicacion_llaves" value="Otro"> Otro</label>
                  </div>
                </div>
                <div class="form-group span-2" v-if="solicitud.ubicacion_llaves === 'Otro'">
                  <input v-model="solicitud.ubicacion_llaves_otro" type="text" placeholder="Especifique otra ubicación" required>
                </div>
              </div>
            </div>

            <!-- Submit -->
            <div class="submit-row">
              <button
                type="submit"
                class="btn-std btn-primary"
                :disabled="visibilityOption==='selected' && selectedProfessionals.length===0"
                :title="visibilityOption==='selected' && selectedProfessionals.length===0 ? 'Selecciona al menos un profesional' : ''"
              >
                Enviar solicitud
                <svg xmlns="http://www.w3.org/2000/svg" class="icon-arrow" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.4 21.6 22 12 3.4 2.4 3 10l12 2-12 2z"/>
                </svg>
              </button>
            </div>
          </section>

          <!-- RIGHT: PROFESSIONALS (sticky) -->
          <aside class="right-col">
            <div class="pro-panel">
              <div class="pro-panel-header">
                <h4>Profesionales</h4>
                <span class="small">Seleccionados: <b>{{ uiSelectedCount }}</b></span>
              </div>

              <!-- Visibility control -->
              <div class="pro-visibility">
                <label class="seg"><input type="radio" value="selected" v-model="visibilityOption"><span>Invitar solo seleccionados</span></label>
                <label class="seg"><input type="radio" value="my" v-model="visibilityOption"><span>Todos mis profesionales</span></label>
                <label class="seg"><input type="radio" value="all" v-model="visibilityOption"><span>Todos los profesionales</span></label>
              </div>

              <!-- “Todos los profesionales” message -->
              <div v-if="inviteModeAll" class="pro-info">
                Todos los profesionales de Jobbing
              </div>

              <div v-else>
                <!-- Filtros (sin comuna) -->
                <div class="pro-filters">
                  <input v-model="proSearch" class="filter-input" type="text" placeholder="Buscar por nombre, habilidad…">
                  <div class="filters-row">
                    <select v-model.number="proMinRating" class="filter-select">
                      <option :value="0">Rating: cualquiera</option>
                      <option :value="4.5">★ 4.5+</option>
                      <option :value="4.8">★ 4.8+</option>
                    </select>
                  </div>
                </div>

                <!-- Chips -->
                <div v-if="uiSelected.length" class="pro-chips">
                  <span v-for="p in uiSelected" :key="'chip-'+p.id" class="pro-chip">
                    <span class="chip-avatar"><img :src="p.avatar_url" :alt="p.nombre"></span>
                    {{ p.nombre }}
                    <button class="chip-x" type="button" @click="removeSelected(p.id)" v-if="inviteModeSelected">×</button>
                  </span>
                </div>

                <!-- Spinner or list -->
                <div v-if="loadingProfesionales" class="pro-loading">
                  <div class="spinner-border text-secondary" role="status"></div>
                </div>
                <ul v-else class="pro-list">
                  <li v-for="p in filteredPros" :key="p.id" class="pro-card">
                    <label class="pro-card-grid">
                      <!-- In 'my' mode: checkboxes appear checked and disabled -->
                      <input
                        class="pro-check"
                        type="checkbox"
                        :value="p.id"
                        v-model="selectedProfessionals"
                        :disabled="!inviteModeSelected"
                      />
                      <img class="pro-avatar" :src="p.avatar_url" :alt="p.nombre">

                      <div class="pro-main">
                        <div @click="$refs.modalPerfil.openByNombreSeo(p.nombre_seo)" class="pro-title">
                          <span class="pro-name">{{ p.nombre }}</span>
                          <span v-if="p.distancia_km" class="pill">{{ p.distancia_km.toFixed(1) }} km</span>
                        </div>

                        <!-- 3) NEW: your badges -->
                        <div class="mp-row mp-gap-small mp-wrap mp-badges">
                          <span v-if="p.puntaje_busqueda > 70" class="mp-chip mp-chip-warn">
                            Top <i class="material-icons mp-chip-icon">emoji_events</i>
                          </span>
                          <span v-if="calcularTiempoActivo(p) < 1440" class="mp-chip mp-chip-info">
                            <span v-if="calcularTiempoActivo(p) < 60">Activo hace minutos</span>
                            <span v-else-if="calcularTiempoActivo(p) < 120">Activo hace 1 hora</span>
                            <span v-else>Activo en las últimas horas</span>
                            <i class="material-icons mp-chip-icon">hourglass_top</i>
                          </span>
                          <span v-if="p.response_time_score > 50" class="mp-chip mp-chip-ok">
                            Respuesta rápida <i class="material-icons mp-chip-icon">schedule</i>
                          </span>
                        </div>

                        <div class="pro-meta">
                          <span v-if="p.clasificacion_n && p.clasificacion_n > 0">
                          <span class="stars">

                            <span class="stars-base">☆☆☆☆☆</span>
                            <span class="stars-fill" :style="{ width: (((p.clasificacion_suma/p.clasificacion_n).toFixed(1)||0)/5*100) + '%' }">★★★★★</span>
                          </span>
                          <span class="meta">{{ (p.clasificacion_suma/p.clasificacion_n||0).toFixed(2) }}</span>
                          </span>
                          <span v-else>Sin calificaciones</span>
                          <span class="dot">·</span>
                          <!-- <span class="meta">{{ p.trabajos }} trabajos</span> -->
                          <span class="dot">·</span>
                          <!-- <span class="meta">{{ p.tasa_respuesta }}</span> -->
                        </div>

                        <div class="pro-desc">{{ p.descripcion }}</div>
                        <div class="pro-tags">
                          <span v-for="cat in p.categorias" :key="cat" class="tag">{{ cat }}</span>
                          <span v-if="p.comuna_nombre" class="tag">{{ p.comuna_nombre }}</span>
                        </div>
                      </div>

                      <div class="pro-right">
                        <div class="pro-price" v-if="p.precio_visita">{{ p.precio_visita }}</div>
                        <div class="pro-availability" v-if="p.disponibilidad">{{ p.disponibilidad }}</div>
                      </div>
                    </label>
                  </li>
                </ul>

                <div v-if="!loadingProfesionales && !filteredPros.length" class="pro-empty">No se encontraron profesionales con esos filtros.</div>
              </div>
            </div>
          </aside>
        </form>
        <!-- ===== /grid ===== -->
      </div> <!-- /content-pad -->
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-overlay"><div class="spinner"></div></div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-content">
        <div style="text-align:center; font-size: 30px !important;">
          <h3><b-icon style="display:inline-block;height:20px;width:20px;" icon="exclamation-circle"></b-icon> Advertencia</h3>
        </div>
        <h3>Ya existe una solicitud activa para esta dirección</h3>
        <p><strong>Título:</strong> {{ ongoingSolicitud.titulo }}</p>
        <p><strong>Fecha:</strong> {{ new Date(ongoingSolicitud.fecha_creado_iso).toLocaleDateString('es-ES', { year: 'numeric', month: 'numeric', day: 'numeric' }) }}</p>
        <p><strong>Descripción:</strong> {{ ongoingSolicitud.descripcion }}</p>
        <button @click="closeModal">Cerrar</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import ModalPerfilProfesional from '@/components/ModalPerfilProfesional.vue';
import TopNav from '@/components/TopNav.vue';

export default {
  name: 'CrearSolicitud',
  components: { SideNav, TopNav, ModalPerfilProfesional},
  data() {
    return {
      // Usuario
      usuario: { nombre: '', email: '', telefono: '', notificar_cliente: false },

      // Solicitud
      solicitud: {
        descripcion: '',
        comuna_id: '',
        prioridad: 'media',
        direccion: '',
        estado: '',
        chat_habilitado: true,
        ubicacion_llaves: '',
        ubicacion_llaves_otro: '',
        tipo_propiedad: '',
        numero_apartamento: '',
      },

      // Autocomplete
      suggestions: [],
      latitude: null,
      longitude: null,
      place_id: null,

      // Estado general
      fotos: [],
      dragOver: false,
      isLoading: false,
      ongoingSolicitud: null,
      showModal: false,

      ubicacionModo: 'comuna',
      propiedades: [],
      propiedadesLoading: false,
      selectedPropiedadId: null,

      // Profesionales (from your provided shape)
      loadingProfesionales: true,
      profesionales: [],
      profList: [],
      visibilityOption: 'my',        // 'selected' | 'my' | 'all'
      selectedProfessionals: [],
      prevManualSelected: [],        // restore when returning to "selected"

      // Filtros (sin comuna)
      proSearch: '',
      proMinRating: 0,
    };
  },
  computed: {
    ...mapGetters(['admin_datos', 'categorias', 'comunas']),

    propertyManagerBrandColor() {
      if (!this.admin_datos || this.admin_datos.user_type !== 'property_manager') {
        return null;
      }

      const atributos = this.admin_datos.empresa && this.admin_datos.empresa.atributos
        ? this.admin_datos.empresa.atributos
        : null;

      const color = atributos && atributos.color ? atributos.color : null;

      if (!this.isValidHexColor(color)) {
        return null;
      }

      return this.normalizeHexColor(color);
    },

    propertyManagerThemeStyles() {
      if (!this.propertyManagerBrandColor) {
        return {};
      }

      const base = this.propertyManagerBrandColor;

      return {
        '--pm-cta-solid': base,
        '--pm-cta-solid-hover': this.adjustColor(base, -0.12),
        '--pm-cta-shadow': `0 12px 28px ${this.hexToRgba(base, 0.24)}`,
        '--pm-cta-shadow-hover': `0 16px 34px ${this.hexToRgba(base, 0.3)}`,
      };
    },

    inviteModeSelected() { return this.visibilityOption === 'selected'; },
    inviteModeMy() { return this.visibilityOption === 'my'; },
    inviteModeAll() { return this.visibilityOption === 'all'; },
    useSavedProperty() {
      return this.ubicacionModo === 'propiedad' && this.propiedades.length > 0;
    },
    selectedPropiedad() {
      if (!this.selectedPropiedadId) {
        return null;
      }
      return this.propiedades.find(item => item.id === this.selectedPropiedadId) || null;
    },

    filteredPros() {
      const term = this.proSearch.trim().toLowerCase();
      return this.profList
        .filter(p => (p.rating || 0) >= (this.proMinRating || 0))
        .filter(p => {
          if (!term) return true;
          return [p.nombre, p.skill, p.comuna_nombre, p.descripcion, p.clasificacion_n, p.clasificacion_suma]
            .join(' ')
            .toLowerCase()
            .includes(term);
        })
        .sort((a, b) => (b.rating || 0) - (a.rating || 0));
    },

    // Chips/contador según modo
    uiSelected() {
      if (this.inviteModeAll) return [];
      if (this.inviteModeMy) return this.profList;
      const ids = new Set(this.selectedProfessionals);
      return this.profList.filter(p => ids.has(p.id));
    },
    uiSelectedCount() {
      return this.inviteModeMy ? this.profList.length : (this.inviteModeAll ? 0 : this.selectedProfessionals.length);
    },
  },
  watch: {
    visibilityOption(newVal) {
      if (newVal === 'my') {
        this.prevManualSelected = this.selectedProfessionals.slice();
        this.selectedProfessionals = this.profList.map(p => p.id);
      } else if (newVal === 'selected') {
        if (this.prevManualSelected && this.prevManualSelected.length) {
          this.selectedProfessionals = this.prevManualSelected.slice();
        }
      } else if (newVal === 'all') {
        this.prevManualSelected = this.selectedProfessionals.slice();
        this.selectedProfessionals = [];
      }
    },
    profList(newVal) {
      if (this.inviteModeMy) {
        this.selectedProfessionals = newVal.map(p => p.id);
      }
    }
  },
  methods: {
    isValidHexColor(color) {
      if (typeof color !== 'string') {
        return false;
      }
      const value = color.trim();
      return /^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(value);
    },
    normalizeHexColor(color) {
      if (!this.isValidHexColor(color)) {
        return null;
      }
      let hex = color.trim().replace('#', '');
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map((char) => `${char}${char}`)
          .join('');
      }
      return `#${hex.toLowerCase()}`;
    },
    adjustColor(hex, ratio) {
      const normalized = this.normalizeHexColor(hex);
      if (!normalized) {
        return hex;
      }

      const clampRatio = Math.max(-1, Math.min(1, ratio));
      const integer = parseInt(normalized.slice(1), 16);
      const channels = [
        (integer >> 16) & 0xff,
        (integer >> 8) & 0xff,
        integer & 0xff,
      ];

      const adjustChannel = (channel) => {
        const delta = clampRatio >= 0 ? (255 - channel) * clampRatio : channel * clampRatio;
        return Math.max(0, Math.min(255, Math.round(channel + delta)));
      };

      const [r, g, b] = channels.map(adjustChannel);
      const toHex = (channel) => channel.toString(16).padStart(2, '0');

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },
    hexToRgba(hex, alpha = 1) {
      const normalized = this.normalizeHexColor(hex);
      if (!normalized) {
        return `rgba(79, 70, 229, ${alpha})`;
      }
      const integer = parseInt(normalized.slice(1), 16);
      const r = (integer >> 16) & 0xff;
      const g = (integer >> 8) & 0xff;
      const b = integer & 0xff;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    // ===== Files (dropzone) =====
    triggerFilePicker() { this.$refs.fileInput && this.$refs.fileInput.click(); },
    onFileChange(e) { this.setFiles(Array.from(e.target.files || [])); },
    handleDrop(e) {
      this.dragOver = false;
      const files = Array.from(e.dataTransfer.files || []);
      this.setFiles(files);
    },
    setFiles(files) {
      // You can limit, dedupe, validate here if needed
      this.fotos = files;
    },
    isImage(file) { return file && file.type && file.type.startsWith('image/'); },
    thumbURL(file) { return URL.createObjectURL(file); },

    // ===== Profesionales normalizer (your exact shape) =====
    normalizeProfesionales(rows) {
      return (rows || []).map((raw, i) => {
        const p = raw.profesional ? raw.profesional : raw;
        const nombre =
          (p.nombre && p.apellido) ? `${p.nombre} ${p.apellido}` :
          p.nombre_completo || p.nombre || `Profesional ${p.id || i+1}`;

        const rating =
          p.clasificacion_n && p.clasificacion_n > 0
            ? p.clasificacion_suma / p.clasificacion_n
            : (typeof p.rating === 'number' ? p.rating : (p.rating_score || 0));

        const categoriaIds = p.categorias || [];
        const categoriaLabels = categoriaIds
          .map(id => {
            const c = this.categorias.find(cat => cat.id === id);
            return c ? c.nombre : '';
          })
          .filter(Boolean);

        return {
          id: p.id,
          nombre,
          avatar_url: p.foto || p.avatar_url || p.foto_url || 'https://via.placeholder.com/96x96.png?text=P',
          rating,
          trabajos: p.total_trabajos || p.jobs || 0,
          tasa_respuesta: (typeof p.reply_score === 'number') ? `${p.reply_score}% respuesta` : (p.tasa_respuesta || '—'),
          descripcion: p.descripcion || 'Profesional verificado.',
          skill: p.habilidades || '',
          comuna_nombre: p.comuna_nombre || p.comuna || '',
          distancia_km: typeof p.distancia_km === 'number' ? p.distancia_km : null,
          precio_visita: p.precio_visita || '',
          disponibilidad: p.disponibilidad || '',
          categorias: categoriaLabels,
          // badges fields:
          puntaje_busqueda: p.puntaje_busqueda || 0,
          response_time_score: p.response_time_score || 0,
          ultima_actividad: p.ultima_actividad || null,
          clasificacion_n: p.clasificacion_n,
          clasificacion_suma: p.clasificacion_suma,
          nombre_seo:p.nombre_seo,
        };
      });
    },

    fetchProfesionales() {
      this.loadingProfesionales = true;
      axios.get(window.hostname + 'api/property_manager/get_profesionales')
        .then(res => {
          this.profesionales = res.data.profesionales || [];
          this.profList = this.normalizeProfesionales(this.profesionales);
        })
        .catch(() => { this.profesionales = []; this.profList = []; })
        .finally(() => { this.loadingProfesionales = false; });
    },

    // ===== Address autocomplete (unchanged) =====
    onInput() {
      if (this.useSavedProperty) {
        return;
      }
      const q = this.solicitud.direccion.trim();
      if (!q) { this.suggestions = []; return; }
      axios.get(window.hostname + `api/property_manager/autocomplete?input=${encodeURIComponent(q)}`)
        .then(res => { this.suggestions = res.data || []; })
        .catch(() => { this.suggestions = []; });
    },

    selectSuggestion(suggestion) {
      axios.get(window.hostname + `api/property_manager/place_details?place_id=${encodeURIComponent(suggestion.place_id)}`)
        .then(res => {
          const data = res.data;
          const comps = data.address_components || [];
          const route = (comps.find(c => c.types.includes('route')) || {}).long_name || '';
          const num = (comps.find(c => c.types.includes('street_number')) || {}).long_name || '';
          const full = `${route} ${num}`.trim();

          const comunaComp = comps.find(c => c.types.includes('administrative_area_level_3'));
          if (comunaComp) {
            const comunaName = comunaComp.long_name;
            const found = this.comunas.find(x => x.Nombre === comunaName);
            if (found) this.solicitud.comuna_id = found.id;
          }

          if (data.geometry && data.geometry.location) {
            this.latitude = data.geometry.location.lat;
            this.longitude = data.geometry.location.lng;
          }
          this.place_id = data.place_id || null;
          this.solicitud.direccion = full;
          this.suggestions = [];

          if (this.place_id) this.checkOngoingSolicitud(this.place_id, this.latitude, this.longitude);
        })
        .catch(() => console.error('Error retrieving place details'));
    },

    checkOngoingSolicitud(place_id) {
      axios.get(window.hostname + `api/property_manager/check_ongoing?place_id=${encodeURIComponent(place_id)}&lat=${this.latitude}&long=${this.longitude}`)
        .then(res => {
          if (res.data && res.data.ongoing) { this.ongoingSolicitud = res.data.solicitud; this.showModal = true; }
          else { this.ongoingSolicitud = null; this.showModal = false; }
        })
        .catch(e => console.error('Error checking ongoing solicitud', e));
    },

    closeModal() { this.showModal = false; },

    fetchPropiedades() {
      this.propiedadesLoading = true;
      axios.get(window.hostname + 'api/property_manager/propiedades')
        .then((res) => {
          const propiedades = res.data?.propiedades || [];
          this.propiedades = propiedades;
          if (!propiedades.length) {
            this.ubicacionModo = 'comuna';
            this.selectedPropiedadId = null;
            return;
          }

          if (this.ubicacionModo !== 'propiedad') {
            this.ubicacionModo = 'propiedad';
          }
          if (!this.selectedPropiedadId) {
            this.selectedPropiedadId = propiedades[0].id;
          }
          const propiedad = propiedades.find(item => item.id === this.selectedPropiedadId);
          if (propiedad) {
            this.applyPropiedadSelection(propiedad);
          }
        })
        .catch(() => {
          this.propiedades = [];
          this.ubicacionModo = 'comuna';
          this.selectedPropiedadId = null;
        })
        .finally(() => {
          this.propiedadesLoading = false;
        });
    },

    handlePropiedadChange() {
      const propiedad = this.propiedades.find(item => item.id === this.selectedPropiedadId);
      if (propiedad) {
        this.applyPropiedadSelection(propiedad);
      }
    },

    applyPropiedadSelection(propiedad) {
      this.solicitud.comuna_id = propiedad.comuna_id || '';
      this.solicitud.direccion = propiedad.direccion || '';
      this.solicitud.tipo_propiedad = propiedad.tipo_propiedad || '';
      this.solicitud.numero_apartamento = propiedad.numero_apartamento || '';
      this.usuario.nombre = propiedad.contacto_nombre || '';
      this.usuario.email = propiedad.contacto_email || '';
      this.usuario.telefono = propiedad.contacto_telefono || '';
      this.suggestions = [];
      this.latitude = null;
      this.longitude = null;
      this.place_id = null;
    },

    setUbicacionModo(modo) {
      if (this.ubicacionModo === modo) return;
      if (modo === 'propiedad' && !this.propiedades.length) return;
      this.ubicacionModo = modo;

      if (modo === 'comuna') {
        this.selectedPropiedadId = null;
        this.solicitud.comuna_id = '';
        this.solicitud.direccion = '';
        this.solicitud.tipo_propiedad = '';
        this.solicitud.numero_apartamento = '';
        this.suggestions = [];
        this.latitude = null;
        this.longitude = null;
        this.place_id = null;
        return;
      }

      if (!this.selectedPropiedadId && this.propiedades.length) {
        this.selectedPropiedadId = this.propiedades[0].id;
      }
      const propiedad = this.propiedades.find(item => item.id === this.selectedPropiedadId);
      if (propiedad) {
        this.applyPropiedadSelection(propiedad);
      }
    },

    formatPropiedadLabel(propiedad) {
      const parts = [propiedad.nombre, propiedad.direccion].filter(Boolean);
      return parts.join(' · ') || 'Propiedad';
    },

    getComunaName(propiedad) {
      if (!propiedad || !propiedad.comuna_id) {
        return '';
      }
      const comuna = this.comunas.find(item => item.id === propiedad.comuna_id);
      return comuna?.Nombre || '';
    },

    removeSelected(id) {
      if (!this.inviteModeSelected) return; // solo permite quitar en modo seleccionado
      this.selectedProfessionals = this.selectedProfessionals.filter(x => x !== id);
    },

    // ===== Badges helper from your snippet =====
    calcularTiempoActivo(userLike) {
      const iso = userLike.ultima_actividad;
      if (!iso) return Infinity;
      const ultimaActividad = new Date(iso);
      const ahora = new Date();
      return Math.floor((ahora - ultimaActividad) / 60000);
    },

    crearSolicitud() {
      // Validaciones base
      if (!this.usuario.nombre || !this.usuario.email || !this.usuario.telefono ||
          !this.solicitud.descripcion ||
          !this.solicitud.comuna_id || !this.solicitud.direccion || !this.solicitud.prioridad ||
          !this.solicitud.estado || !this.solicitud.ubicacion_llaves || !this.solicitud.tipo_propiedad) {
        alert('Por favor, complete todos los campos obligatorios.');
        return;
      }
      if (this.solicitud.tipo_propiedad.toLowerCase() === 'departamento' && !this.solicitud.numero_apartamento) {
        alert('Por favor, ingrese el número de departamento.');
        return;
      }
      if (this.visibilityOption === 'selected' && this.selectedProfessionals.length === 0) {
        alert('Por favor, seleccione al menos un profesional.');
        return;
      }
      if (this.solicitud.ubicacion_llaves === 'Otro' && !this.solicitud.ubicacion_llaves_otro) {
        alert('Por favor, especifique la ubicación de llaves.');
        return;
      }

      this.isLoading = true;

      const fd = new FormData();
      // usuario
      fd.append('nombre', this.usuario.nombre);
      fd.append('email', this.usuario.email);
      fd.append('telefono', this.usuario.telefono);
      fd.append('notificar_cliente', this.usuario.notificar_cliente);

      // solicitud
      fd.append('descripcion', this.solicitud.descripcion);
      fd.append('comuna_id', this.solicitud.comuna_id);
      fd.append('prioridad', this.solicitud.prioridad);
      fd.append('direccion', this.solicitud.direccion);
      fd.append('estado', this.solicitud.estado);
      fd.append('chat_habilitado', this.solicitud.chat_habilitado);
      fd.append('tipo_propiedad', this.solicitud.tipo_propiedad);

      if (this.solicitud.tipo_propiedad.toLowerCase() === 'departamento') {
        fd.append('numero_apartamento', this.solicitud.numero_apartamento);
      }

      // opcionales
      // llaves
      if (this.solicitud.ubicacion_llaves === 'Otro') fd.append('ubicacion_llaves', this.solicitud.ubicacion_llaves_otro);
      else fd.append('ubicacion_llaves', this.solicitud.ubicacion_llaves);

      // geo
      if (this.latitude !== null && this.longitude !== null) { fd.append('lat', this.latitude); fd.append('lng', this.longitude); }
      if (this.place_id) fd.append('place_id', this.place_id);

      // visibilidad y seleccionados (NO cambiamos tu backend)
      fd.append('visibilidad_profesionales', this.visibilityOption);
      if (this.visibilityOption === 'selected') fd.append('profesional_ids', this.selectedProfessionals.join(','));

      if (this.useSavedProperty && this.selectedPropiedadId) {
        fd.append('propiedad_configuracion_id', this.selectedPropiedadId);
      }

      // fotos
      this.fotos.forEach((f, i) => fd.append(`foto${i}`, f));

      axios.post(window.hostname + 'api/property_manager/crear_solicitud', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(() => {
          this.isLoading = false;
          this.$notify({ group: 'foo', title: '¡Éxito!', text: '¡Solicitud creada exitosamente!', type: 'success' });
          this.$router.push('/administrador-propiedades/Solicitudes');
        })
        .catch((e) => {
          this.isLoading = false;
          console.log('Error al crear la solicitud', e);
          alert('Ocurrió un error al crear la solicitud');
        });
    },
  },
  mounted() {
    if (!this.admin_datos || this.admin_datos.user_type !== 'property_manager') {
      this.$router.push('/');
    } else {
      this.fetchProfesionales();
      this.fetchPropiedades();
    }
  },
};
</script>

<style scoped>
/* Base layout */
.crear-solicitud-page {
  text-align: left;
  --pm-cta-solid: #4f46e5;
  --pm-cta-solid-hover: #4338ca;
  --pm-cta-shadow: 0 6px 16px rgba(79, 70, 229, 0.25);
  --pm-cta-shadow-hover: 0 8px 22px rgba(79, 70, 229, 0.3);
}

.column-right { background:#f2f7fb; padding-top:80px; padding-left:260px; width:100%; }
@media (max-width: 800px) { .column-right { padding-left: 0; } }

/* 1) NEW padding wrapper */
.content-pad { padding:10px; }

.page-heading h2 { font-weight:700; font-size:24px; margin:10px 0 16px; }

/* Two-column grid */
.new-solicitud-grid { display:grid; grid-template-columns: 2fr 1fr; gap:20px; }
@media (max-width: 1100px) { .new-solicitud-grid { grid-template-columns: 1fr; } }
.left-col { display:flex; flex-direction:column; gap:16px; }
.right-col { position: relative; }

/* Cards */
.card { background:#fff; border:1px solid #e3e8ef; border-radius:14px; box-shadow:0 1px 2px rgba(0,0,0,.04); }
.card-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid #e9eef5; }
.card-header h3 { margin:0; font-size:16px; font-weight:700; }
.badge { font-size:11px; color:#64748b; background:#f1f5f9; padding:4px 9px; border-radius:999px; }
.card-body { padding:20px 22px; display:grid; gap:12px; }
.card-body.two-col { grid-template-columns: 1fr 1fr; }
.card-body .span-2 { grid-column: 1 / -1; }
.card-body .span-1-2 { grid-column: 1 / -1; }
@media (max-width: 700px){ .card-body.two-col { grid-template-columns: 1fr; } }

/* Segment-like chips */
.seg {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #dbe3ec;
  border-radius: 10px;
  background: #fbfdff;
  cursor: pointer;
  white-space: nowrap;
}
.seg input { margin: 0; }
.form-group .seg { align-self: flex-start; }
.chat-toggle { padding-top: 22px; }

/* Form controls */
.form-group { display:flex; flex-direction:column; gap:6px; }
.form-group label { font-weight:600; }
.form-group input, .form-group select, .form-group textarea {
  width:100%; padding:10px; border:1px solid #cfd6dd; border-radius:8px; box-sizing:border-box; outline: none;
}
.form-group textarea { resize:vertical; }

.location-toggle {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.location-toggle__btn {
  border: 1px solid #dbe3ec;
  border-radius: 10px;
  padding: 8px 12px;
  background: #fbfdff;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}

.location-toggle__btn.is-active {
  border-color: var(--pm-cta-solid, #4f46e5);
  color: var(--pm-cta-solid, #4f46e5);
  background: rgba(79, 70, 229, 0.08);
}

.location-toggle__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.property-summary {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  gap: 8px;
}

.property-summary__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  text-transform: uppercase;
}

.property-summary__value {
  color: #0f172a;
  font-weight: 600;
}

.helper-text {
  color: #64748b;
  font-size: 0.85rem;
  margin: 4px 0 0;
}


/* Address autocomplete */
.address-group { position:relative; }
.suggestions {
  list-style:none; position:absolute; left:0; right:0; top:100%;
  background:#fff; border:1px solid #cfd6dd; border-radius:8px; padding:0; margin:6px 0 0; max-height:200px; overflow:auto; z-index:999;
}
.suggestions li { padding:8px 10px; border-bottom:1px solid #eef2f6; cursor:pointer; }
.suggestions li:hover { background:#f8fafc; }

/* 2) Dropzone styles */
.dropzone {
  border:2px dashed #cfd6dd; border-radius:12px; padding:22px; text-align:center; transition: background .15s, border-color .15s;
}
.dropzone.is-dragover { background:#f8fafc; border-color:#9aa7b5; }
.dz-text { margin:0; color:#667085; }
.link-btn { background:transparent; border:0; color:#4f46e5; cursor:pointer; text-decoration:underline; padding:0 2px; }
.thumbs { margin-top:12px; display:flex; gap:10px; flex-wrap:wrap; }
.thumb { width:110px; border:1px solid #e6eaf0; border-radius:10px; padding:8px; display:flex; flex-direction:column; align-items:center; gap:6px; background:#fff; }
.thumb img { width:100%; height:70px; object-fit:cover; border-radius:6px; background:#f2f2f2; }
.thumb-icon { width:100%; height:70px; display:flex; align-items:center; justify-content:center; font-size:28px; color:#6b7280; background:#f3f4f6; border-radius:6px; }
.thumb-name { font-size:12px; color:#475569; text-align:center; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; width:100%; }

/* Submit row + prettier button */
.submit-row { display:flex; justify-content:flex-end; margin-top:8px; }
.btn-std.btn-primary {
  display:inline-flex; align-items:center; gap:8px;
  background: var(--pm-cta-solid, #4f46e5) !important;
  color:#fff !important;
  padding:12px 18px;
  border:0 !important;
  border-radius:12px;
  font-weight:600;
  box-shadow: var(--pm-cta-shadow, 0 6px 16px rgba(79,70,229,.25));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-std.btn-primary:hover:not(:disabled) {
  background: var(--pm-cta-solid-hover, #4338ca) !important;
  box-shadow: var(--pm-cta-shadow-hover, 0 8px 22px rgba(79,70,229,.3));
  transform: translateY(-1px);
}
.btn-std.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.icon-arrow { width:20px; height:20px; }

/* Sticky right panel */
.pro-panel { position: sticky; top:90px; background:#fff; border:1px solid #e3e8ef; border-radius:14px; overflow:hidden; }
.pro-panel-header { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; border-bottom:1px solid #e9eef5; }
.pro-panel-header h4 { margin:0; font-size:15px; font-weight:700; }
.small { color:#607182; font-size:12px; }

/* Visibility control area */
.pro-visibility { display:flex; gap:8px; flex-wrap:wrap; padding:12px 14px; border-bottom:1px solid #eef2f6; }
.pro-info { padding:12px 14px; color:#4b5563; border-bottom:1px solid #eef2f6; }

/* Filters (sin comuna) */
.pro-filters { padding:12px 14px; border-bottom:1px solid #eef2f6; display:flex; flex-direction:column; gap:8px; }
.filter-input { padding:10px; border:1px solid #cfd6dd; border-radius:10px; outline: none; }
.filters-row { display:flex; gap:8px; }
.filter-select { flex:1; padding:10px; border:1px solid #cfd6dd; border-radius:10px; }

/* Chips */
.pro-chips { display:flex; flex-wrap:wrap; gap:6px; padding:8px 14px 0; }
.pro-chip { display:inline-flex; align-items:center; gap:6px; padding:6px 8px; border-radius:999px; background:#eef2ff; color:#28327a; font-size:12px; }
.chip-avatar { width:18px; height:18px; border-radius:4px; overflow:hidden; display:inline-flex; }
.chip-avatar img { width:100%; height:100%; object-fit:cover; }
.chip-x { background:transparent; border:0; margin-left:2px; cursor:pointer; font-size:14px; color:#28327a; }

/* Professional list */
.pro-list { list-style:none; margin:8px 0 0; padding:12px 12px 14px; max-height:68vh; overflow:auto; display:flex; flex-direction:column; gap:10px; }
.pro-card { border:1px solid #e4e8ee; border-radius:12px; background:#fff; }
.pro-card-grid { display:grid; grid-template-columns: auto 56px 1fr auto; gap:12px; align-items:start; padding:10px; }
.pro-check { margin-top:4px; }
.pro-avatar { width:56px; height:56px; border-radius:10px; object-fit:cover; background:#f2f2f2; }
.pro-main { min-width:0; }
.pro-title { display:flex; align-items:center; gap:8px; margin-bottom:4px; cursor: pointer;}
.pro-name { font-weight:600; }
.pill { font-size:12px; padding:2px 6px; background:#f3f4f6; border-radius:999px; color:#4b5563; }

/* Your badge container; your classes can already exist app-wide */
.mp-row.mp-badges { display:flex; gap:6px; flex-wrap:wrap; margin:4px 0; }
.mp-chip { display:inline-flex; align-items:center; gap:6px; padding:3px 8px; border-radius:999px; font-size:12px; }
.mp-chip-warn { background:#fff7ed; color:#9a3412; border:1px solid #fed7aa; }
.mp-chip-info { background:#e0f2fe; color:#075985; border:1px solid #bae6fd; }
.mp-chip-ok { background:#dcfce7; color:#166534; border:1px solid #bbf7d0; }
.mp-chip-icon { font-size:14px; }

.pro-meta { display:flex; align-items:center; flex-wrap:wrap; gap:6px; color:#5b6472; font-size:13px; }
.meta { color:#5b6472; }
.dot { opacity:.6; }
.stars { position:relative; display:inline-block; line-height:1; }
.stars-base, .stars-fill { letter-spacing:2px; font-size:14px; }
.stars-base { color:#c9d1db; }
.stars-fill { color:#f59e0b; position:absolute; top:0; left:0; white-space:nowrap; overflow:hidden; }
.pro-desc { margin-top:6px; color:#5b6472; font-size:13px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.pro-tags { display:flex; gap:6px; flex-wrap:wrap; margin-top:6px; }
.tag { font-size:11px; padding:3px 6px; background:#f3f4f6; color:#374151; border-radius:999px; }
.tag.skill { background:#e8f0ff; color:#1d4ed8; }
.pro-right { text-align:right; min-width:120px; }
.pro-price { font-weight:600; }
.pro-availability { font-size:12px; color:#6b7280; }
.pro-empty { padding:12px; text-align:center; color:#6b7280; }
.pro-loading { padding:20px; text-align:center; }

/* Loading + modal */
.loading-overlay { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:9999; }
.spinner { border:12px solid #f3f3f3; border-top:12px solid #3498db; border-radius:50%; width:60px; height:60px; animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:10000; }
.modal-content { background:#fff; padding:20px; border-radius:8px; max-width:500px; width:100%; }
</style>
